import { useCallback } from 'react'
import { ActivityState } from '@/api/models/activity'
import { ActivityWorkflowState } from '@/api/models/aggregate'
import { activityConstraintValidation } from '@/hooks/useActivityParts/helpers/activityConstraintValidation'
import { useActiveActivityPartAxis } from '@/hooks/useActivityParts/useActiveActivityPartAxis'
import { useIsNotAccessibleActivityPart } from '@/hooks/useIsNotAccessibleActivityPart'
import type { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import type { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'

type TOCMenuItemParams = {
  isSelected: boolean
  isCompleted: boolean
  isDraft: boolean
  isLocked: boolean
  isExpanded: boolean
  isPassed?: boolean | null
  isOptional: boolean
}

type PropsInput = {
  activityPart?: FrontActivityPart<FrontActivityWithAggregate>
}

export const useTOCMenuItem = () => {
  const activeAxis = useActiveActivityPartAxis()
  const isNotAccessible = useIsNotAccessibleActivityPart()

  const properties = useCallback(
    ({ activityPart }: PropsInput): TOCMenuItemParams => {
      if (!activityPart) {
        console.warn('useTOCMenuItem: missing activityPart')
        return {
          isSelected: false,
          isCompleted: false,
          isDraft: false,
          isLocked: false,
          isExpanded: false,
          isPassed: false,
          isOptional: false,
        }
      }

      const { childActivity } = activityPart
      const workflow = childActivity.activityWorkflowAggregate?.lastActivityWorkflow
      const bestRecord = childActivity.activityWorkflowAggregate?.bestActivityRecord
      const axis = activityPart.axis

      const isSelected = activeAxis === axis

      const isCompleted = workflow?.state === ActivityWorkflowState.finished
      const isDraft = childActivity.state === ActivityState.draft

      const isNotDraftAndNotAvailable = !isDraft && !activityPart.available
      const isDisabledByConstraint = activityConstraintValidation(activityPart).disabled
      const isLocked = isNotDraftAndNotAvailable || isDisabledByConstraint || isNotAccessible(activityPart)

      const isExpanded = Array.isArray(activeAxis) ? activeAxis.includes(axis) : false

      const isPassed = bestRecord?.passed
      const isOptional = Boolean(activityPart.optional)

      return {
        isSelected,
        isCompleted,
        isDraft,
        isLocked,
        isExpanded,
        isPassed,
        isOptional,
      }
    },
    [activeAxis, isNotAccessible],
  )

  return properties
}
